import { Building2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../auth/AuthContext';

/**
 * Wraps a single tenant-scoped route (Overview, Assistants, Knowledge,
 * Conversations, Usage, API keys — anything gated by `require_active_tenant`
 * on the backend). Everything else (Team, Workspaces, Settings) stays
 * reachable with no active tenant at all: an OWNER/ADMIN must be able to get
 * into the app and add staff before any workspace exists, so this guards one
 * page's content, not the whole shell.
 */
export function RequireWorkspace({ children }: { children: ReactNode }) {
  const { session } = useAuth();

  if (session && !session.tenant) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
          <span className="grid size-12 place-items-center rounded-full bg-primary-muted text-primary ring-1 ring-primary/20">
            <Building2 className="size-6" aria-hidden />
          </span>
          <div>
            <p className="text-sm font-medium">No active workspace</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Create or switch to a workspace to use this section.
            </p>
          </div>
          <Button asChild>
            <Link to="/tenants">Go to Workspaces</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
}
